// What the Loading page shows about each item's sync with PDI (the marker beside its status) and what it
// says after a save. Kept out of the page so the rules can be tested on their own.

import { LOADING_ITEMS, normalizePdiStatus, pdiToLoading, type ItemSyncResult, type LoadingStatus, type SyncState } from '@technohull/shared';

// The server's state is worked out for the statuses it saw; an item changed here since then is re-read
// against PDI's value, so an edit shows "Syncing" (or already "In sync") at once instead of the state
// from before the edit.
export type Mark = Exclude<SyncState, 'pdi-unknown'> | 'unknown' | 'none' | 'syncing';

export function markFor(server: SyncState | null | undefined, pdiValue: string | null | undefined, status: LoadingStatus): Mark {
  if (server == null) return 'none';
  if (server === 'pdi-unknown') return 'unknown';
  const p = normalizePdiStatus(pdiValue);
  if (p === null) return 'unknown';
  if (pdiToLoading(p) === status) return p === 'Hold' ? 'pdi-hold' : 'in-sync';
  // they differ: the server already knew (Loading changed after PDI, or PDI is about to win), or the
  // item was changed here after the last read and the refresh is on its way
  return server === 'in-sync' || server === 'pdi-hold' ? 'syncing' : server;
}

/** a PDI Status as words: blank reads as "blank", not as nothing. */
const pdiWord = (v: string | null | undefined) => (v == null ? 'nothing' : v === '' ? 'blank' : v);

export type MarkTone = 'is-ok' | 'is-hold' | 'is-info' | 'is-none';
export type MarkIcon = 'check' | 'hatch' | 'refresh' | null;
export interface MarkView {
  tone: MarkTone;
  icon: MarkIcon;
  label: string;
  /** the longer sentence: the tooltip on desktop, a quiet note on a phone card. */
  note: string;
}

/** how a marker looks and what it says. A PDI hold always shows as a hold (amber, hatched), also when
 *  Loading has moved on and is waiting to reach PDI, so the hold is visible without hovering. */
export function markView(
  mark: Mark,
  pdiValue: string | null | undefined,
  opts: { canEditPdi: boolean; paused?: boolean },
): MarkView {
  const held = normalizePdiStatus(pdiValue) === 'Hold';
  switch (mark) {
    case 'in-sync':
      return { tone: 'is-ok', icon: 'check', label: 'In sync', note: `Matches PDI (PDI: ${pdiWord(pdiValue)})` };
    case 'pdi-hold':
      return { tone: 'is-hold', icon: 'hatch', label: 'PDI hold', note: 'PDI has this item on hold, so it stays Pending here until the hold clears' };
    case 'not-in-pdi':
      if (held) {
        return {
          tone: 'is-hold',
          icon: 'hatch',
          label: 'PDI hold',
          note: `Changed here, but PDI has this item on hold and keeps it until the hold is cleared there${opts.canEditPdi ? ' or this item is sent to PDI' : " (you can't edit PDI)"}`,
        };
      }
      return { tone: 'is-info', icon: 'refresh', label: 'Not in PDI yet', note: `Changed here after PDI. PDI still says ${pdiWord(pdiValue)}${opts.canEditPdi ? '' : " (you can't edit PDI)"}` };
    case 'pdi-newer':
      return {
        tone: held ? 'is-hold' : 'is-info',
        icon: held ? 'hatch' : 'refresh',
        label: held ? 'PDI hold' : 'Updating from PDI',
        note: opts.paused
          ? `PDI changed later (PDI: ${pdiWord(pdiValue)}), but some PDI sheets couldn't be read just now, so this item follows it once they can`
          : `PDI changed later (PDI: ${pdiWord(pdiValue)}); this item follows it on the next refresh`,
      };
    case 'syncing':
      return { tone: 'is-info', icon: 'refresh', label: 'Syncing', note: 'Saved here; checking PDI' };
    case 'unknown':
      return { tone: 'is-none', icon: null, label: `PDI: ${pdiValue ?? ''}`, note: `PDI says "${pdiValue ?? ''}", which the sync doesn't read, so this item isn't synced` };
    case 'none':
      return { tone: 'is-none', icon: null, label: 'No PDI item', note: 'No PDI row has this name. An admin can add the PDI wording as an alias in Admin › Mappings' };
  }
}

// ── notices after a save ──────────────────────────────────────────────────────
export interface Notice { tone: 'ok' | 'warn' | 'err'; text: string }

/** a failed write's reason in plain words. */
function reasonText(r: string): string {
  const why = r.slice('failed:'.length);
  if (why === 'pdi-unavailable') return "PDI couldn't be read just now";
  if (why === 'rate-limited') return 'too many PDI changes in a minute, try again shortly';
  return why || 'unknown error';
}

/** what to tell the person about PDI after a Loading save. Only failures and kept holds are worth a
 *  notice (plus the count when they asked to send items to PDI); a synced or unmatched item says
 *  nothing, and neither does an item the save didn't change (null). */
export function noticeFor(results: ReadonlyArray<readonly [number, ItemSyncResult | null]>, opts: { push?: boolean } = {}): Notice | null {
  const list = results.filter((x): x is readonly [number, ItemSyncResult] => x[1] !== null);
  const failed = list.filter(([, r]) => r.startsWith('failed:'));
  const kept = list.filter(([, r]) => r === 'kept-pdi-hold');
  const synced = list.filter(([, r]) => r === 'synced');
  const named = (l: ReadonlyArray<readonly [number, ItemSyncResult]>) =>
    l.length <= 2 ? l.map(([i]) => LOADING_ITEMS[i]).join(' and ') : `${l.length} items`;
  const parts: string[] = [];
  if (opts.push) parts.push(synced.length ? `Sent ${synced.length} item${synced.length === 1 ? '' : 's'} to PDI.` : 'Nothing was sent to PDI.');
  else if (failed.length || kept.length) parts.push('Saved here.');
  if (failed.length) {
    const reasons = [...new Set(failed.map(([, r]) => reasonText(r)))];
    parts.push(`PDI wasn't updated for ${named(failed)}: ${reasons.join('; ')}.`);
  }
  if (kept.length) parts.push(`PDI keeps its hold on ${named(kept)}.`);
  if (!parts.length || (!opts.push && !failed.length && !kept.length)) return null;
  return { tone: failed.length ? 'err' : kept.length ? 'warn' : 'ok', text: parts.join(' ') };
}
